import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AccountProvider } from './contexts/AccountContext';
import { Layout } from './components/Layout';
import { Dashboard } from './pages/Dashboard';
import { RecordAudio } from './pages/RecordAudio';
import { RecordVideo } from './pages/RecordVideo';
import { UploadFile } from './pages/UploadFile';
import { EntryDetail } from './pages/EntryDetail';
import { Changelog } from './pages/Changelog';
import { Share } from './pages/Share';
import { Login } from './pages/Login';
import { AuthSuccess } from './pages/AuthSuccess';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AccountProvider>
          <BrowserRouter>
            <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/auth/success" element={<AuthSuccess />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/record/audio"
              element={
                <ProtectedRoute>
                  <Layout>
                    <RecordAudio />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/record/video"
              element={
                <ProtectedRoute>
                  <Layout>
                    <RecordVideo />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/record/upload"
              element={
                <ProtectedRoute>
                  <Layout>
                    <UploadFile />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/entry/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <EntryDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/share"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Share />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/changelog"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Changelog />
                  </Layout>
                </ProtectedRoute>
              }
            />
            </Routes>
          </BrowserRouter>
        </AccountProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
